import { ArrowRight, MessageCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ListingCard } from "@/components/ListingCard";
import { CURATED_COLLECTIONS, getCuratedCollection } from "@/data/curatedCollections";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";

export default function CollectionLandingPage() {
  const { collectionSlug } = useParams();
  const [listings, setListings] = useState([]);
  const collection = getCuratedCollection(collectionSlug);
  const { branding, buildBrandedPath, getWhatsAppHref, leadFormPath, requestParams } = useSiteBranding();

  useSeo({
    title: collection ? `${collection.title} | SG Prop Portal` : "Curated Collection | SG Prop Portal",
    description: collection?.description || "Curated Singapore property collections for higher-conviction discovery.",
  });

  const loadListings = useCallback(async () => {
    if (!collection) {
      setListings([]);
      return;
    }
    try {
      const params = collection.category ? { category: collection.category, limit: 500 } : { limit: 500 };
      const response = await api.get("/listings", { params: { ...params, ...requestParams } });
      setListings(response.data);
    } catch {
      setListings([]);
    }
  }, [collection, requestParams]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const filteredListings = useMemo(() => {
    if (!collection) return [];
    return listings.filter((listing) => {
      const categoryMatch = collection.category ? listing.category === collection.category : true;
      const districtMatch = collection.districts.length ? collection.districts.includes(listing.district) : true;
      return categoryMatch && districtMatch;
    });
  }, [collection, listings]);

  if (!collection) {
    return (
      <section className="section-shell">
        <div className="rounded-[30px] border border-[#001935]/10 bg-white/80 p-8 shadow-[0_18px_60px_rgba(0,25,53,0.08)]" data-testid="collection-not-found-state">
          <p className="subtle-label">Collection Not Found</p>
          <h1 className="mt-3 text-4xl font-semibold text-[#001935]">This curated page isn’t available yet.</h1>
          <Link className="gold-button mt-6" data-testid="collection-not-found-home-link" to={buildBrandedPath("/")}>
            Return Home
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div>
      <section className="section-shell pt-10">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-[30px] border border-[#001935]/10 bg-white/85 p-8 shadow-[0_22px_80px_rgba(0,25,53,0.08)]" data-testid={`collection-hero-${collection.slug}`}>
            <p className="subtle-label">{collection.eyebrow}</p>
            <h1 className="mt-3 text-4xl font-semibold text-[#001935] md:text-6xl" data-testid="collection-hero-heading">
              {collection.title}
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-[#42566f]" data-testid="collection-hero-description">
              {collection.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a className="gold-button" data-testid="collection-whatsapp-button" href={getWhatsAppHref(`Hello, I’d like to discuss the ${collection.title}.`)} rel="noreferrer" target="_blank">
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp the Team
              </a>
              <Link className="outline-button" data-testid="collection-lead-form-link" to={leadFormPath}>
                Send Enquiry <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="grid gap-4">
            {collection.heroStats.map((item) => (
              <div className="rounded-[28px] border border-[#D4AF37]/35 bg-[#fffaf1] px-5 py-5 shadow-[0_18px_50px_rgba(0,25,53,0.06)]" data-testid={`collection-stat-${item.label.toLowerCase().replace(/\s+/g, "-")}`} key={item.label}>
                <p className="subtle-label">{item.label}</p>
                <p className="mt-3 text-2xl font-semibold text-[#001935]">{item.value}</p>
              </div>
            ))}
            <div className="rounded-[28px] border border-[#001935]/10 bg-[#001935] p-5 text-white shadow-[0_18px_50px_rgba(0,25,53,0.12)]" data-testid="collection-direct-contact-card">
              <p className="subtle-label">Direct Contact</p>
              <p className="mt-3 text-2xl font-semibold">{branding.name}</p>
              <p className="mt-3 text-sm leading-7 text-slate-300">WhatsApp: {branding.whatsapp_display}</p>
              <p className="text-sm leading-7 text-slate-300">Email: {branding.email}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell pt-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="subtle-label">Curated Inventory</p>
            <h2 className="section-heading mt-2">{filteredListings.length} listings in this collection.</h2>
          </div>
          <Link className="outline-button" data-testid="collection-all-listings-link" to={buildBrandedPath("/listings")}>
            Browse All Listings
          </Link>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          {filteredListings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      </section>
    </div>
  );
}